/**
 * История бренда (§7, вкладка внизу карточки).
 *
 * Вертикальный таймлайн по этому бренду, группировка по пакетам, ссылка на
 * пакет ведёт в журнал (`/history/:id`). Фильтр «только это поле» включается
 * кликом по подписи поля в блоке — состояние фильтра держит карточка.
 *
 * Про формат ответа: `GET /api/brands/:id/history` отдаёт `{ items, total }`,
 * где у каждой правки добавлены `changeSetId`, `authorId`, `committedAt` и
 * `note` пакета. В типе `api.brandHistory` стоит `{ changes }` — расхождение
 * описано в отчёте модуля, здесь ответ разбирается по факту, без `any`.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { Change, UserSummary } from '@shared/types.ts'
import { Avatar, Badge, EmptyState, Skeleton } from '../../../components/index.js'
import { api, ApiRequestError } from '../../../lib/api.js'
import { useApp } from '../../../store/app-store.js'
import { cardTexts } from './texts.card.js'
import './HistoryBlock.css'

/** Правка из истории бренда: правка плюс поля её пакета. */
export interface BrandHistoryItem extends Change {
  changeSetId: number | null
  authorId: number | null
  committedAt: string | null
  note: string | null
}

interface HistoryPayload {
  items: BrandHistoryItem[]
  total: number
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** Разбор ответа: и `{items,total}` сервера, и `{changes}` из типа клиента. */
/**
 * Сколько правок у бренда. Нужно подписи вкладки, которую видно до того, как
 * историю откроют: раньше счётчик показывал ноль, пока на вкладку не нажмут.
 */
export async function fetchHistoryCount(brandId: number): Promise<number> {
  return parseHistory(await api.brandHistory(brandId)).total
}

function parseHistory(payload: unknown): HistoryPayload {
  if (!isRecord(payload)) return { items: [], total: 0 }
  const raw = Array.isArray(payload.items)
    ? payload.items
    : Array.isArray(payload.changes)
      ? payload.changes
      : []
  const items = raw.filter(isRecord).map((row): BrandHistoryItem => {
    const change = row as unknown as Change
    return {
      ...change,
      changeSetId: typeof row.changeSetId === 'number' ? row.changeSetId : null,
      authorId: typeof row.authorId === 'number' ? row.authorId : null,
      committedAt: typeof row.committedAt === 'string' ? row.committedAt : null,
      note: typeof row.note === 'string' ? row.note : null,
    }
  })
  return { items, total: typeof payload.total === 'number' ? payload.total : items.length }
}

function timeLabel(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const day = date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  return `${day} в ${time}`
}

/** Подпись значения: `newLabel` сервера, иначе «пусто». */
function valueLabel(label: string | null, value: unknown): string {
  if (label !== null && label !== '' && !label.startsWith('{')) return label
  if (value === null || value === undefined || value === '') return cardTexts.history.empty
  if (typeof value === 'boolean') return value ? cardTexts.value.yes : cardTexts.value.no
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  return cardTexts.history.empty
}

/** Что именно случилось: у attach/comment/create значение — объект, не строка. */
function describe(item: BrandHistoryItem): { title: string; detail: string | null } {
  const payload = isRecord(item.newValue) ? item.newValue : null

  switch (item.action) {
    case 'create':
      return { title: cardTexts.history.action.create, detail: null }
    case 'archive':
      return { title: cardTexts.history.action.archive, detail: null }
    case 'restore':
      return { title: cardTexts.history.action.restore, detail: null }
    case 'attach': {
      const name = typeof payload?.category === 'string' ? payload.category : ''
      return { title: cardTexts.history.action.attach(name), detail: null }
    }
    case 'detach': {
      const name = typeof payload?.category === 'string' ? payload.category : ''
      return { title: cardTexts.history.action.detach(name), detail: null }
    }
    case 'comment': {
      const body = typeof payload?.body === 'string' ? payload.body : ''
      return { title: cardTexts.history.action.comment, detail: body }
    }
    case 'delete_comment':
      return { title: cardTexts.history.action.deleteComment, detail: null }
    case 'update':
    default: {
      const field = item.fieldLabel ?? item.fieldCode ?? ''
      const from = valueLabel(item.oldLabel, item.oldValue)
      const to = valueLabel(item.newLabel, item.newValue)
      return { title: field, detail: `${from} ${cardTexts.history.arrow} ${to}` }
    }
  }
}

interface HistoryPack {
  changeSetId: number | null
  authorId: number | null
  committedAt: string | null
  note: string | null
  items: BrandHistoryItem[]
}

function groupByPack(items: BrandHistoryItem[]): HistoryPack[] {
  const packs: HistoryPack[] = []
  for (const item of items) {
    const last = packs[packs.length - 1]
    if (last && last.changeSetId === item.changeSetId) {
      last.items.push(item)
      continue
    }
    packs.push({
      changeSetId: item.changeSetId,
      authorId: item.authorId,
      committedAt: item.committedAt,
      note: item.note,
      items: [item],
    })
  }
  return packs
}

function authorName(users: UserSummary[], authorId: number | null, note: string | null): string {
  if (authorId === null) return cardTexts.history.systemAuthor
  const found = users.find((user) => user.id === authorId)
  if (found) return found.shortName
  return note ?? cardTexts.history.unknownAuthor
}

export interface HistoryBlockProps {
  brandId: number
  /** Код поля, по которому отфильтрован таймлайн (клик по подписи поля, §7). */
  fieldFilter: { code: string; label: string } | null
  onClearFilter: () => void
  /** Сколько правок в истории — для подписи вкладки. */
  onCountChange?: (total: number) => void
}

export function HistoryBlock({ brandId, fieldFilter, onClearFilter, onCountChange }: HistoryBlockProps) {
  const users = useApp((state) => state.bootstrap?.users ?? [])
  const canSeeHistory = useApp(
    (state) => state.bootstrap?.user.permissions.includes('history.view') ?? false,
  )
  const [payload, setPayload] = useState<HistoryPayload | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!canSeeHistory) return
    let alive = true
    setPayload(null)
    setError(null)
    void api
      .brandHistory(brandId)
      .then((res: unknown) => {
        if (!alive) return
        const parsed = parseHistory(res)
        setPayload(parsed)
        onCountChange?.(parsed.total)
      })
      .catch((err: unknown) => {
        if (!alive) return
        setPayload({ items: [], total: 0 })
        setError(err instanceof ApiRequestError ? err.message : cardTexts.history.loadFailed)
      })
    return () => {
      alive = false
    }
  }, [brandId, canSeeHistory, onCountChange])

  const packs = useMemo(() => {
    if (!payload) return []
    const items = fieldFilter
      ? payload.items.filter((item) => item.fieldCode === fieldFilter.code)
      : payload.items
    return groupByPack(items)
  }, [payload, fieldFilter])

  if (!canSeeHistory) {
    return <p className="card-history__denied t-meta">{cardTexts.history.denied}</p>
  }

  if (payload === null) {
    return <Skeleton rows={3} columns={[180, 220]} label={cardTexts.state.loadingLabel} />
  }

  return (
    <div className="card-history">
      {fieldFilter ? (
        <div className="card-history__filter">
          <Badge variant="accent">{cardTexts.history.onlyField(fieldFilter.label)}</Badge>
          <button type="button" className="card-history__clear t-meta" onClick={onClearFilter}>
            {cardTexts.history.onlyFieldClear}
          </button>
        </div>
      ) : null}

      {error ? <p className="card-history__error t-meta">{error}</p> : null}

      {packs.length === 0 && !error ? (
        <EmptyState title={cardTexts.history.emptyTitle} text={cardTexts.history.emptyText} />
      ) : (
        <ol className="card-history__packs">
          {packs.map((pack, index) => (
            <li className="card-history__pack" key={`${pack.changeSetId ?? 'draft'}-${index}`}>
              <div className="card-history__pack-head">
                <Avatar
                  name={authorName(users, pack.authorId, pack.note)}
                  size={24}
                  system={pack.authorId === null}
                />
                <span className="card-history__pack-meta t-meta">
                  {pack.changeSetId !== null ? (
                    <Link
                      className="card-history__pack-link"
                      to={`/history/${pack.changeSetId}`}
                      aria-label={cardTexts.history.packLabel(pack.changeSetId)}
                    >
                      {cardTexts.history.pack(pack.changeSetId)}
                    </Link>
                  ) : null}
                  <span> {authorName(users, pack.authorId, pack.note)}</span>
                  <span> · {timeLabel(pack.committedAt)}</span>
                  <span> · {cardTexts.history.changes(pack.items.length)}</span>
                </span>
              </div>
              <ul className="card-history__changes">
                {pack.items.map((item) => {
                  const described = describe(item)
                  return (
                    <li className="card-history__change" key={item.id}>
                      <span className="card-history__change-title t-cell">{described.title}</span>
                      {described.detail ? (
                        <span className="card-history__change-detail t-meta">{described.detail}</span>
                      ) : null}
                      <span className="card-history__change-entity t-meta">{item.entityLabel}</span>
                    </li>
                  )
                })}
              </ul>
            </li>
          ))}
        </ol>
      )}

      {payload.total > payload.items.length ? (
        <p className="card-history__more t-meta">
          {cardTexts.history.more(payload.total)}{' '}
          <Link to="/history">{cardTexts.history.openFull}</Link>
        </p>
      ) : null}
    </div>
  )
}
